import { useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { useQueryClient } from 'react-query'

import { useAuth } from '../../../contexts/AuthContext'
import { NoContent } from '../../../components/NoContent'
import { LineData } from '../../../components/LineData'
import { useExpertBankDataMutation } from '../../../services/hooks/experts/mutations/useExpertBankData'
import { useBankDataQuery } from '../../../services/hooks/experts/queries/useExpertBankDataQuery'
import { Spinner } from '../../../components/Spinner'
import { LinkButton } from '../../../components/LinkButton'
import { useExpertQuery } from '../../../services/hooks/experts/queries/useExpertQuery'
import { useExpertInternationalBankDataMutation } from '../../../services/hooks/experts/mutations/useExpertInternationalBankDataMutation'
import { getErrorMessage } from '../../../utils/getErrorMessage'
import { BankDataForm } from '../../../components/Forms/BankDataForm'
import { InternationalBankDataForm } from '../../../components/Forms/InternationalBankDataForm'
import { type InternationalBankDataPersistence } from '../../../types/persistence/international-person-persistence'
import {
  TOAST_SUCCESS_DEFAULT_OPTIONS,
  TOAST_ERROR_DEFAULT_OPTIONS,
} from '../../../constants/toast'
import { Medias } from '../../../enumns/medias'
import { useMediaQuery } from '../../../hooks/useMediaQuery'
import { Button } from '../../../components/Button'

type SaveExpertBankData = {
  bank: string
  bankBranch: string
  currentAccount: string
  pix: string
}

export const BankData = () => {
  const queryClient = useQueryClient()
  const isMinScreenWidthMD = useMediaQuery(`(min-width: ${Medias.MD}px)`)

  const [isFormOpen, setIsFormOpen] = useState(false)

  const { user } = useAuth()
  const { data, isLoading } = useBankDataQuery(user?.id!)
  const { data: userData } = useExpertQuery(user?.id!)
  const { mutate, isLoading: isExpertBankDataLoading } =
    useExpertBankDataMutation()
  const { formatMessage } = useIntl()
  const { mutate: mutateInternational } =
    useExpertInternationalBankDataMutation()

  const handleSaveExpertBankData = (data: SaveExpertBankData) => {
    const expertId = user?.id
    Object.assign(data, { paymentType: userData?.paymentType })

    if (expertId) {
      mutate(
        { id: expertId, data },
        {
          onSuccess(data) {
            const successMessage = formatMessage({
              id: 'BANK_DATA_SUCCESSFULLY_SAVED',
            })

            toast.success(successMessage, TOAST_SUCCESS_DEFAULT_OPTIONS)

            queryClient.setQueryData(['expert', expertId, 'bank-data'], {
              bank: data.bank,
              currentAccount: data.currentAccount,
              bankBranch: data.bankBranch,
            })

            queryClient.invalidateQueries('expert')

            setIsFormOpen(false)
          },
          onError(error) {
            toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
          },
        }
      )
    }
  }

  const handleInternationBankDataSubmit = (
    info: InternationalBankDataPersistence
  ) => {
    if (user?.id) {
      mutateInternational(
        { id: user.id, data: info },
        {
          onSuccess(result) {
            toast(
              formatMessage({ id: 'SAVED_SUCCESSFULLY' }),
              TOAST_SUCCESS_DEFAULT_OPTIONS
            )

            queryClient.setQueryData(['expert', user.id, 'bank-data'], {
              swiftCode: result.swiftCode,
              bankAccountNumber: result.bankAccountNumber,
              bankBranch: result.bankBranch,
              amazonGiftCardEmail: result.amazonGiftCardEmail,
            })

            queryClient.invalidateQueries('expert')

            setIsFormOpen(false)
          },
          onError(error: unknown) {
            toast(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
          },
        }
      )
    }
  }

  console.log(user)

  return (
    <div className="flex flex-col h-full grow lg:px-52">
      <header className="flex items-center justify-between mb-10">
        <h1 className="text-base font-medium md:text-lg">
          {formatMessage({ id: 'BANK_DATA' })}
        </h1>

        {data && !isFormOpen && isMinScreenWidthMD && (
          <Button onClick={() => setIsFormOpen(true)}>
            {formatMessage({ id: 'EDIT' })}
          </Button>
        )}
      </header>

      {user?.id && (
        <p className="mb-8">
          {formatMessage({
            id:
              user.bankCountry === 'brazil'
                ? 'CHECK_CAREFULLY_YOUR_BANK_DATA'
                : 'FINISH_YOUR_INTERNATIONAL_BANK_ACCOUNT_REGISTRATION',
          })}
        </p>
      )}

      <div className="flex flex-col grow">
        <div className="grow">
          {!isFormOpen && !data && (
            <div className="flex items-center justify-center grow">
              {isLoading ? (
                <Spinner />
              ) : (
                <div className="flex flex-col">
                  <NoContent
                    message={formatMessage({ id: 'NO_REGISTERED_ACCOUNT' })}
                  />
                  <div className="mt-[72px] flex flex-col items-center">
                    <p className="mb-[53px] text-sm md:text-lg">
                      {formatMessage({
                        id: 'COMPLETE_YOUR_REGISTRATION_TO_ADD_AN_ACCOUNT',
                      })}
                    </p>
                    <LinkButton to="/expert/complete-registration" size="large">
                      {formatMessage({ id: 'COMPLETE_REGISTRATION' })}
                    </LinkButton>
                  </div>
                </div>
              )}
            </div>
          )}

          {!isFormOpen && data && (
            <>
              {userData?.personType === 'international_person' ? (
                <div>
                  <div>
                    <LineData
                      label={formatMessage({ id: 'SWIFT_CODE' })}
                      text={data.swiftCode}
                    />
                    <LineData
                      label={formatMessage({ id: 'ACCOUNT_NUMBER_OR_IBAN' })}
                      text={data.bankAccountNumber}
                    />
                    <LineData
                      label={formatMessage({ id: 'INTERNATIONAL_BANK_BRANCH' })}
                      text={data.internationalBankBranch}
                    />
                    <LineData
                      label={formatMessage({ id: 'AMAZON_GIFT_CARD_EMAIL' })}
                      text={data.amazonGiftCardEmail}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <LineData
                    label={formatMessage({ id: 'BANK' })}
                    text={data.bank}
                  />
                  <div>
                    <LineData
                      label={formatMessage({ id: 'BANK_BRANCH' })}
                      text={data.bankBranch}
                    />
                    <LineData
                      label={formatMessage({ id: 'CURRENT_ACCOUNT' })}
                      text={data.currentAccount}
                    />
                    <LineData
                      label={formatMessage({ id: 'PIX' })}
                      text={data.pix}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {isFormOpen && (
            <>
              {userData?.personType === 'international_person' ? (
                <div>
                  <header className="flex flex-col mb-10 mt-[-40px]">
                    <p>
                      {formatMessage({
                        id: 'CAREFULLY_ENTER_YOUR_BANK_DETAILS',
                      })}
                    </p>
                  </header>

                  <InternationalBankDataForm
                    defaultValues={{
                      swiftCode: data?.swiftCode ?? '',
                      bankAccountNumber: data?.bankAccountNumber ?? '',
                      internationalBankBranch:
                        data?.internationalBankBranch ?? '',
                      amazonGiftCardEmail: data?.amazonGiftCardEmail ?? '',
                    }}
                    onSubmit={handleInternationBankDataSubmit}
                    onCancel={() => setIsFormOpen(false)}
                    isSubmitting={isExpertBankDataLoading}
                    userId={userData?.id}
                  />
                </div>
              ) : (
                <BankDataForm
                  isSubmitting={isExpertBankDataLoading}
                  onSubmit={handleSaveExpertBankData}
                  onCancel={() => setIsFormOpen(false)}
                  defaultValues={data}
                  userId={userData?.id}
                />
              )}
            </>
          )}
        </div>

        {data && !isFormOpen && !isMinScreenWidthMD && (
          <Button onClick={() => setIsFormOpen(true)}>
            {formatMessage({ id: 'EDIT' })}
          </Button>
        )}
      </div>
    </div>
  )
}
